/**
 * Shared domain table components
 */
import React from 'react';
import { Trash2, Info, RefreshCw, Link2Off, Settings, CheckCircle2, ShieldCheck, Loader2, Clock, ExternalLink, LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { APP_CONFIG } from '@/config/app.config';

// Status types
type DomainStatus = 'active' | 'verified' | 'pending' | 'ssl-pending';

interface StatusConfig {
  label: string;
  variant: 'success' | 'warning' | 'default';
  icon: LucideIcon;
  animate?: boolean;
}

const STATUS_CONFIG: Record<DomainStatus, StatusConfig> = {
  active: {
    label: 'Active',
    variant: 'success',
    icon: CheckCircle2,
  },
  verified: {
    label: 'Verified',
    variant: 'success',
    icon: ShieldCheck,
  },
  'ssl-pending': {
    label: 'SSL Pending',
    variant: 'warning',
    icon: Loader2,
    animate: true,
  },
  pending: {
    label: 'Unverified',
    variant: 'warning',
    icon: Clock,
  },
};

/**
 * Determine domain status from domain properties
 */
export function getDomainStatus(domain: {
  type: 'subdomain' | 'custom';
  verified: boolean;
  routingConfigured?: boolean;
  sslStatus?: string;
  siteId?: string | null;
}): DomainStatus {
  if (domain.type === 'subdomain' && domain.siteId) return 'active';
  if (domain.verified && domain.sslStatus === 'active') return 'active';
  if (domain.verified && domain.sslStatus === 'pending') return 'ssl-pending';
  if (domain.verified) return 'verified';
  return 'pending';
}

/**
 * Status badge for domains
 */
interface DomainStatusBadgeProps {
  status: DomainStatus;
  className?: string;
}

export const DomainStatusBadge: React.FC<DomainStatusBadgeProps> = ({ status, className }) => {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className={className}>
      <Icon className={`h-3 w-3 mr-1 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

/**
 * Type badge (subdomain or custom)
 */
interface DomainTypeBadgeProps {
  type: 'subdomain' | 'custom';
  className?: string;
}

export const DomainTypeBadge: React.FC<DomainTypeBadgeProps> = ({ type, className }) => (
  <span className={`text-sm text-muted-foreground capitalize ${className ?? ''}`}>{type}</span>
);

/**
 * Domain link with external icon
 */
interface DomainLinkProps {
  domain: string;
  type: 'subdomain' | 'custom';
  showIcon?: boolean;
  className?: string;
}

export const DomainLink: React.FC<DomainLinkProps> = ({
  domain,
  type,
  className = ''
}) => {
  const displayDomain = type === 'subdomain'
    ? `${domain}.${APP_CONFIG.SITES_DOMAIN}`
    : domain;
  const url = `https://${displayDomain}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 font-medium text-foreground hover:underline ${className}`}
    >
      {displayDomain}
      <ExternalLink className="h-3.5 w-3.5 stroke-[1.5] opacity-70" aria-hidden="true" />
    </a>
  );
};

/**
 * Domain cell with globe icon and link
 */
interface DomainCellProps {
  domain: string;
  type: 'subdomain' | 'custom';
}

export const DomainCell: React.FC<DomainCellProps> = ({ domain, type }) => (
  <DomainLink domain={domain} type={type} />
);

/**
 * Delete domain button
 */
interface DeleteDomainButtonProps {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}

export const DeleteDomainButton: React.FC<DeleteDomainButtonProps> = ({
  onClick,
  disabled,
  title = 'Delete domain'
}) => (
  <Button
    size="dense"
    variant="tertiary"
    onClick={onClick}
    disabled={disabled}
    className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
    title={title}
  >
    <Trash2 className="h-4 w-4" />
    Delete
  </Button>
);

/**
 * Verify domain button
 */
interface VerifyDomainButtonProps {
  onClick: () => void;
  isVerifying?: boolean;
  title?: string;
}

export const VerifyDomainButton: React.FC<VerifyDomainButtonProps> = ({
  onClick,
  isVerifying,
  title = 'Check DNS verification'
}) => (
  <Button
    size="dense"
    variant="tertiary"
    onClick={onClick}
    disabled={isVerifying}
    title={title}
  >
    <RefreshCw className={`h-4 w-4 ${isVerifying ? 'animate-spin' : ''}`} />
    Verify
  </Button>
);

/**
 * DNS info button
 */
interface DnsInfoButtonProps {
  onClick: () => void;
  title?: string;
}

export const DnsInfoButton: React.FC<DnsInfoButtonProps> = ({
  onClick,
  title = 'View DNS instructions'
}) => (
  <Button
    size="dense"
    variant="tertiary"
    onClick={onClick}
    title={title}
  >
    <Info className="h-4 w-4" />
    Records
  </Button>
);

/**
 * Configure DNS button
 */
interface ConfigureDnsButtonProps {
  onClick: () => void;
}

export const ConfigureDnsButton: React.FC<ConfigureDnsButtonProps> = ({ onClick }) => (
  <Button
    size="dense"
    variant="tertiary"
    onClick={onClick}
  >
    <Settings className="h-4 w-4 mr-1" />
    Configure
  </Button>
);

/**
 * Detach domain button
 */
interface DetachDomainButtonProps {
  onClick: () => void;
}

export const DetachDomainButton: React.FC<DetachDomainButtonProps> = ({ onClick }) => (
  <Button
    size="dense"
    variant="tertiary"
    onClick={onClick}
    className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
    title="Disconnect domain"
  >
    <Link2Off className="h-4 w-4" />
    Disconnect
  </Button>
);
